import React, { useEffect, useMemo, useState } from "react";

// First-run proposal build. The agentic analysis (shred → design → architect →
// finalize) runs 1–4 minutes in a worker and streams progress over the status
// socket. This used to be a `toast.loading()` — a single line of text for a
// multi-minute wait.
//
// Renders as an overlay ON the draft page (blurred behind) rather than replacing
// it, so you can see the workspace you're about to land in.
//
// Every stage below maps to a REAL progress frame the backend emits
// (rfp/agents/toc_team.py `_emit` + rfp/tasks/analysis.py). Nothing here is
// invented: if the backend stops sending a frame, the stage simply never lights.

const STAGES = [
  {
    at: 5,
    label: "Reading",
    title: "Reading your documents",
    blurb: "Parsing every uploaded file and pulling out the text we can work from.",
  },
  {
    at: 25,
    label: "Requirements",
    title: "Shredding RFP requirements",
    blurb:
      "Breaking the RFP into individual, numbered requirements so nothing gets missed later.",
  },
  {
    at: 45,
    label: "Skeleton",
    title: "Designing the skeleton",
    blurb: "Deciding which sections your proposal needs to answer every requirement.",
  },
  {
    at: 65,
    label: "Outline",
    title: "Architecting the outline",
    blurb:
      "Ordering sections and subsections, and writing the brief each one will be drafted from.",
  },
  {
    at: 90,
    label: "Finalizing",
    title: "Finalizing the outline",
    blurb: "Numbering, de-duplicating, and checking the structure holds together.",
  },
];

const stageIndexFor = (percent) => {
  let idx = 0;
  STAGES.forEach((s, i) => {
    if (percent >= s.at) idx = i;
  });
  return idx;
};

const CheckIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Minimized form, shown when the user has navigated off the Draft step. The
// build keeps running server-side either way; this is the ambient reminder that
// it is still going, and a way back to the full view.
export const ProposalBuildingPill = ({ progress, onOpen }) => {
  const pct = typeof progress?.percent === "number" ? progress.percent : 5;
  const stage = STAGES[stageIndexFor(pct)];
  return (
    <button
      type="button"
      onClick={onOpen}
      title="Back to the build"
      className="fixed bottom-6 right-6 z-40 flex items-center gap-3 rounded-full border border-[#ECE9EE] bg-white pl-3 pr-4 py-2.5 shadow-[0_10px_30px_-12px_rgba(32,10,28,0.4)] hover:border-[#A04A97] transition-colors"
    >
      <span className="relative w-7 h-7 shrink-0">
        <svg viewBox="0 0 36 36" className="w-7 h-7 -rotate-90">
          <circle cx="18" cy="18" r="15.5" fill="none" stroke="#F1EAF0" strokeWidth="5" />
          <circle
            cx="18" cy="18" r="15.5" fill="none"
            stroke="#A04A97" strokeWidth="5" strokeLinecap="round"
            strokeDasharray={`${(pct / 100) * 97.4} 97.4`}
            style={{ transition: "stroke-dasharray 600ms ease-out" }}
          />
        </svg>
      </span>
      <span className="text-left leading-tight">
        <span className="block font-outfit text-[12.5px] font-semibold text-[#18101C]">
          Building your proposal
        </span>
        <span className="block font-outfit text-[11.5px] text-[#6B7280]">
          {stage.title} · {Math.round(pct)}%
        </span>
      </span>
    </button>
  );
};

const ProposalBuildingView = ({ progress, documentCount = 0 }) => {
  const reported = typeof progress?.percent === "number" ? progress.percent : 5;
  const stageIndex = stageIndexFor(reported);
  const stage = STAGES[stageIndex];

  // Frames arrive ~30–60s apart, so a bar pinned to the last one looks frozen.
  // Creep toward the NEXT stage but stop short of it: the track keeps moving
  // without ever claiming a stage finished before the backend says so.
  const ceiling = STAGES[stageIndex + 1]?.at ?? 97;
  const [eased, setEased] = useState(reported);
  useEffect(() => {
    setEased((v) => Math.max(v, reported));
    const t = setInterval(() => {
      setEased((v) => (v >= ceiling - 1 ? v : v + Math.max(0.08, (ceiling - 1 - v) / 90)));
    }, 400);
    return () => clearInterval(t);
  }, [reported, ceiling]);
  const shown = Math.min(99, Math.max(reported, eased));

  // Past 4 minutes, stop promising "1–3 min" — say something honest instead.
  const [overrunning, setOverrunning] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setOverrunning(true), 240000);
    return () => clearTimeout(t);
  }, []);

  const subtitle = useMemo(() => {
    const docs = documentCount
      ? `${documentCount} document${documentCount === 1 ? "" : "s"}`
      : "your documents";
    return overrunning
      ? `Still working on ${docs} — long or scanned RFPs take a little longer.`
      : `Analyzing ${docs} · usually 1–3 minutes`;
  }, [documentCount, overrunning]);

  return (
    <div className="absolute inset-0 z-30 rounded-[10px] bg-white/55 backdrop-blur-[3px] flex items-center justify-center p-6">
      <div className="w-full max-w-[540px] rounded-2xl border border-[#ECE9EE] bg-white shadow-[0_18px_50px_-20px_rgba(32,10,28,0.35)] px-7 py-6">
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="font-outfit text-[19px] font-semibold text-[#18101C] leading-tight">
            Building your proposal
          </h2>
          <span className="font-outfit text-[13px] font-semibold text-[#A04A97] tabular-nums shrink-0">
            {Math.round(shown)}%
          </span>
        </div>
        <p className="font-outfit text-[13px] text-[#6B7280] mt-1">{subtitle}</p>

        {/* Horizontal stepper. The connector between two dots fills with real
            progress through that stage, so the fill IS the loading animation —
            no separate bar competing with it. */}
        <div className="mt-6 flex items-start">
          {STAGES.map((s, i) => {
            const done = i < stageIndex;
            const active = i === stageIndex;
            const next = STAGES[i + 1];
            const span = next ? next.at - s.at : 0;
            const fill = next
              ? Math.max(0, Math.min(1, (shown - s.at) / (span || 1)))
              : 0;
            return (
              <React.Fragment key={s.label}>
                <div className="flex flex-col items-center gap-2 shrink-0 w-[62px]">
                  <span
                    className={`w-[20px] h-[20px] rounded-full flex items-center justify-center transition-colors duration-500 ${
                      done
                        ? "bg-[#A04A97] text-white"
                        : active
                        ? "bg-white border-2 border-[#A04A97]"
                        : "bg-white border-2 border-[#E4E4E7]"
                    }`}
                  >
                    {done ? (
                      <CheckIcon />
                    ) : (
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          active ? "bg-[#A04A97] animate-ping" : "bg-[#E4E4E7]"
                        }`}
                      />
                    )}
                  </span>
                  <span
                    className={`font-outfit text-[10.5px] leading-tight text-center ${
                      done || active ? "text-[#3F3F46] font-medium" : "text-[#A1A1AA]"
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {next && (
                  <div className="flex-1 h-[3px] mt-[9px] rounded-full bg-[#EFEFF1] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#A04A97]"
                      style={{
                        width: `${fill * 100}%`,
                        transition: "width 600ms ease-out",
                      }}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Only the running stage explains itself — five blurbs at once is a
            wall nobody reads. */}
        <div className="mt-6">
          <div className="font-outfit text-[15px] font-semibold text-[#18101C]">
            {stage.title}
          </div>
          <p className="font-outfit text-[13px] text-[#6B7280] mt-1">{stage.blurb}</p>
        </div>

        {/* The backend's own words, verbatim — ground truth beneath our
            friendlier stage titles. */}
        <div className="mt-5 pt-4 border-t border-[#F2F0F4]">
          <p className="font-outfit text-[12.5px] text-[#9CA3AF] truncate">
            {progress?.message || stage.title}
          </p>
          <p className="font-outfit text-[12.5px] text-[#9CA3AF] mt-0.5">
            You can leave this page — the build carries on without you.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProposalBuildingView;
